import { useState } from "react";
import { toast } from "sonner";
import { Share2, Copy, Check, QrCode, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

/* Single share-by-link button used on every public Portfolio page header.
 * Per user choice (1b + 2c): one obvious button that always shares whatever
 * the visitor is currently looking at (URL includes any active filters), with
 * Copy + QR Code only (no SMS/email). */
const ShareButton = ({ label = "Share this view", testid = "share-btn" }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // We always share the *current* URL — captures any tag filter or industry
  // slug — but force production host once deployed so a screenshot of a QR
  // taken on the preview env doesn't leak.
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy — long-press the URL field instead");
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-sm transition border-2 hover:bg-[color:var(--ups-muted)]"
        style={{ borderColor: "var(--ups-ink)", background: "#fff" }}
        data-testid={testid}
        aria-label={label}
        title={label}
      >
        <Share2 size={14} /> Share
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="share-modal"
        >
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="relative w-full max-w-md rounded-sm overflow-hidden"
            style={{ background: "#fff", border: "2px solid var(--ups-ink)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3" style={{ background: "var(--ups-ink)", color: "#FFB500" }}>
              <h3 className="font-display text-[17px] font-bold flex items-center gap-2">
                <Share2 size={16} /> Share this view
              </h3>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-sm hover:bg-white/10" aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Copy URL row */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)]">Share link</label>
                <div className="mt-1 flex items-stretch gap-2">
                  <input
                    type="text"
                    readOnly
                    value={shareUrl}
                    onFocus={(e) => e.target.select()}
                    className="input-swiss flex-1 px-3 py-2 rounded-sm text-[12px] font-mono"
                    data-testid="share-url-input"
                  />
                  <button
                    onClick={handleCopy}
                    className="btn-brutal px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5"
                    data-testid="share-copy-btn"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                <p className="text-[10px] text-[color:var(--ups-subtext)] mt-1.5 leading-snug">
                  The link captures the page you're on (industry + any active tag filter). Send it however you like.
                </p>
              </div>

              {/* QR code */}
              <div className="pt-3" style={{ borderTop: "1px dashed var(--ups-border)" }}>
                <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)] flex items-center gap-1.5">
                  <QrCode size={11} /> Scan with phone
                </label>
                <div className="mt-2 flex items-center gap-4">
                  <div
                    className="p-2 rounded-sm shrink-0"
                    style={{ background: "#fff", border: "2px solid var(--ups-ink)" }}
                    data-testid="share-qr"
                  >
                    <QRCodeSVG value={shareUrl} size={148} level="M" includeMargin={false} />
                  </div>
                  <p className="text-[11px] text-[color:var(--ups-subtext)] leading-snug">
                    Have your customer point their phone camera at the code — most cameras auto-open the link.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareButton;
